import React, { useState, useRef, useEffect } from "react";
import { useQuery, gql } from "@apollo/client";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useAcknowledgedTerms } from "../hooks/useAcknowledgedTerms";
import { API_URL } from "../config";

const AVAILABILITY_QUERY = gql`
  query BookingAvailability($saunaId: Int!) {
    saunaById(id: $saunaId) {
      availabilityRulesBySaunaId(orderBy: DAY_OF_WEEK_ASC) {
        nodes { dayOfWeek startTime endTime }
      }
      availabilityBlocksBySaunaId { nodes { startAt endAt } }
      bookingsBySaunaId { nodes { startAt endAt status } }
    }
  }
`;

interface Props {
  saunaId: number;
  ownerId: number | undefined;
  pricePerHourCents: number;
  requiresApproval: boolean;
  minBookingHours: number;
  maxBookingHours: number;
}

interface Rule {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}

function parseHour(timeStr: string): number {
  return parseInt(timeStr.split(":")[0], 10);
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function isPast(date: Date): boolean {
  return date < startOfDay(new Date());
}

function buildTimestamp(date: Date, hour: number): string {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour, 0, 0, 0).toISOString();
}

function toDateStr(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function parseDateStr(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]));
  return isNaN(d.getTime()) ? null : d;
}

export default function BookingWidget({
  saunaId,
  ownerId,
  pricePerHourCents,
  requiresApproval,
  minBookingHours,
  maxBookingHours,
}: Props) {
  const { userId: currentUserId } = useAuth();
  const { acknowledged: termsOk } = useAcknowledgedTerms(currentUserId);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const dateParam  = searchParams.get("date") ?? "";
  const startParam = searchParams.get("start");
  const hoursParam = searchParams.get("hours");

  const initDate  = dateParam  ? parseDateStr(dateParam)  : null;
  const initHour  = startParam ? parseInt(startParam, 10) : null;
  const initHours = hoursParam ? parseInt(hoursParam, 10) : null;

  const [calMonth, setCalMonth] = useState(() => {
    if (initDate) return new Date(initDate.getFullYear(), initDate.getMonth(), 1);
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedHour, setSelectedHour] = useState<number | null>(null);
  const [duration, setDuration] = useState(
    initHours && initHours >= minBookingHours && initHours <= maxBookingHours
      ? initHours
      : minBookingHours
  );
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [booking, setBooking] = useState(false);
  const seededRef = useRef(false);

  const isOwner = currentUserId === ownerId;

  const { data, loading } = useQuery(AVAILABILITY_QUERY, {
    variables: { saunaId },
    fetchPolicy: "network-only",
    skip: isOwner,
  });

  const rules: Rule[] = data?.saunaById?.availabilityRulesBySaunaId?.nodes ?? [];
  const blocks: { startAt: string; endAt: string }[] =
    data?.saunaById?.availabilityBlocksBySaunaId?.nodes ?? [];
  const existingBookings: { startAt: string; endAt: string; status: string }[] =
    (data?.saunaById?.bookingsBySaunaId?.nodes ?? []).filter(
      (b: any) => !["cancelled", "declined"].includes(b.status)
    );

  function ruleForDow(dow: number): Rule | undefined {
    return rules.find(r => r.dayOfWeek === dow);
  }

  function availableStartHours(date: Date): number[] {
    const rule = ruleForDow(date.getDay());
    if (!rule) return [];
    const ruleStart = parseHour(rule.startTime);
    const ruleEnd   = parseHour(rule.endTime);
    const hours: number[] = [];
    for (let h = ruleStart; h <= ruleEnd - minBookingHours; h++) {
      const slotStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), h, 0, 0, 0);
      const slotEnd   = new Date(slotStart.getTime() + 60 * 60 * 1000);
      const conflictsBooking = existingBookings.some(b => {
        const bs = new Date(b.startAt).getTime();
        const be = new Date(b.endAt).getTime();
        return bs < slotEnd.getTime() && be > slotStart.getTime();
      });
      const conflictsBlock = blocks.some(b => {
        const bs = new Date(b.startAt).getTime();
        const be = new Date(b.endAt).getTime();
        return bs < slotEnd.getTime() && be > slotStart.getTime();
      });
      if (!conflictsBooking && !conflictsBlock) hours.push(h);
    }
    return hours;
  }

  function allRuleHours(date: Date): number[] {
    const rule = ruleForDow(date.getDay());
    if (!rule) return [];
    const ruleStart = parseHour(rule.startTime);
    const ruleEnd   = parseHour(rule.endTime);
    const hours: number[] = [];
    for (let h = ruleStart; h <= ruleEnd - minBookingHours; h++) hours.push(h);
    return hours;
  }

  function isDayAvailable(date: Date): boolean {
    if (isPast(date)) return false;
    const rule = ruleForDow(date.getDay());
    if (!rule) return false;
    const dayStart = buildTimestamp(date, parseHour(rule.startTime));
    const dayEnd   = buildTimestamp(date, parseHour(rule.endTime));
    const blocked  = blocks.some(b =>
      new Date(b.startAt) <= new Date(dayStart) && new Date(b.endAt) >= new Date(dayEnd)
    );
    if (blocked) return false;
    return availableStartHours(date).length > 0;
  }

  function maxDuration(date: Date, startHour: number): number {
    const rule = ruleForDow(date.getDay());
    if (!rule) return maxBookingHours;
    const ruleEnd = parseHour(rule.endTime);
    let max = Math.min(maxBookingHours, ruleEnd - startHour);
    for (let h = startHour + 1; h <= startHour + max; h++) {
      const slotStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), h, 0, 0, 0);
      const slotEnd   = new Date(slotStart.getTime() + 60 * 60 * 1000);
      const conflict  = [...existingBookings, ...blocks].some(b => {
        const bs = new Date(b.startAt).getTime();
        const be = new Date(b.endAt).getTime();
        return bs < slotEnd.getTime() && be > slotStart.getTime();
      });
      if (conflict) { max = h - startHour; break; }
    }
    return max;
  }

  // Seed date/time from URL params once data loads (used after login redirect).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (seededRef.current || !data || !initDate) return;
    seededRef.current = true;
    if (isDayAvailable(initDate)) {
      setSelectedDate(initDate);
      setCalMonth(new Date(initDate.getFullYear(), initDate.getMonth(), 1));
      if (initHour !== null) {
        const avail = availableStartHours(initDate);
        if (avail.includes(initHour)) {
          setSelectedHour(initHour);
          if (initHours !== null) {
            const max = maxDuration(initDate, initHour);
            setDuration(Math.max(minBookingHours, Math.min(initHours, max)));
          }
        }
      }
    }
  }, [data]); // intentionally only on data load

  // Owner guard — must come after all hooks
  if (isOwner) return null;

  async function handleBook() {
    if (!selectedDate || selectedHour === null) return;

    if (!currentUserId) {
      const nextUrl = `/saunas/${saunaId}?date=${toDateStr(selectedDate)}&start=${selectedHour}&hours=${duration}`;
      navigate(`/login?next=${encodeURIComponent(nextUrl)}`);
      return;
    }

    setBookingError(null);
    setBooking(true);
    const startIso = buildTimestamp(selectedDate, selectedHour);
    const endIso   = buildTimestamp(selectedDate, selectedHour + duration);
    try {
      const token = sessionStorage.getItem("jwt");
      const res = await fetch(`${API_URL}/api/stripe/create-payment-intent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          saunaId,
          startAt: startIso,
          endAt: endIso,
          localDow: selectedDate.getDay(),
          localStartHour: selectedHour,
          localEndHour: selectedHour + duration,
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        setBookingError(json.error ?? "Booking failed. Please try again.");
        return;
      }
      navigate(`/checkout/${json.bookingId}`, {
        state: {
          clientSecret: json.clientSecret,
          requiresApproval: json.requiresApproval,
          breakdown: json.breakdown,
        },
      });
    } catch (err: any) {
      setBookingError(err?.message ?? "Booking failed. Please try again.");
    } finally {
      setBooking(false);
    }
  }

  // ── Calendar helpers ──
  const calYear   = calMonth.getFullYear();
  const calMonthN = calMonth.getMonth();
  const daysInMonth = new Date(calYear, calMonthN + 1, 0).getDate();
  const firstDow    = new Date(calYear, calMonthN, 1).getDay();
  const calTitle    = calMonth.toLocaleString("en-US", { month: "long", year: "numeric" });
  const today       = startOfDay(new Date());

  function prevMonth() { setCalMonth(new Date(calYear, calMonthN - 1, 1)); setSelectedDate(null); setSelectedHour(null); }
  function nextMonth() { setCalMonth(new Date(calYear, calMonthN + 1, 1)); setSelectedDate(null); setSelectedHour(null); }

  const hasRules   = rules.length > 0;
  const startHours = selectedDate ? availableStartHours(selectedDate) : [];
  const totalCents = selectedHour !== null ? duration * pricePerHourCents : null;

  return (
    <div style={widgetContainer}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <span style={{ fontSize: 24, fontWeight: 700 }}>
          ${(pricePerHourCents / 100).toFixed(2)} / hr
        </span>
        {requiresApproval && (
          <span style={{ fontSize: 12, background: "#fef3c7", color: "#92400e", padding: "2px 8px", borderRadius: 12 }}>
            Requires approval
          </span>
        )}
      </div>

      {loading && <p style={{ fontSize: 13, color: "#9ca3af" }}>Loading availability...</p>}

      {!loading && !hasRules && (
        <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "12px 0" }}>
          This sauna has no availability set yet.
        </p>
      )}

      {!loading && hasRules && (
        <>
          {/* Month navigation */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <button onClick={prevMonth} style={navBtn}>‹</button>
            <span style={{ fontSize: 14, fontWeight: 600 }}>{calTitle}</span>
            <button onClick={nextMonth} style={navBtn}>›</button>
          </div>

          {/* Calendar grid */}
          <div style={calGrid}>
            {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => (
              <div key={d} style={{ textAlign: "center", fontSize: 11, color: "#9ca3af", fontWeight: 600 }}>
                {d}
              </div>
            ))}
            {Array.from({ length: firstDow }).map((_, i) => <div key={`e${i}`} />)}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day  = i + 1;
              const date = new Date(calYear, calMonthN, day);
              const avail = isDayAvailable(date);
              const sel   = selectedDate?.getTime() === date.getTime();
              const past  = date < today;
              return (
                <button
                  key={day}
                  disabled={!avail}
                  onClick={() => {
                    setSelectedDate(date);
                    setSelectedHour(null);
                    setDuration(minBookingHours);
                  }}
                  style={{
                    ...dayBtn,
                    background: sel ? "#2563EB" : avail ? "#eff6ff" : "transparent",
                    color: sel ? "#fff" : past || !avail ? "#c4c9d4" : "#1d4ed8",
                    cursor: avail ? "pointer" : "default",
                    fontWeight: sel ? 700 : 400,
                    transform: "none",
                    boxShadow: sel ? "inset 0 0 0 2px #fff, 0 0 0 2px #1d4ed8" : "none",
                    opacity: past && !avail ? 0.45 : 1,
                  }}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div style={{ display: "flex", gap: 14, marginTop: 10, flexWrap: "wrap" }}>
            {[
              { bg: "#eff6ff", border: "#bfdbfe", label: "Available" },
              { bg: "#f3f4f6", border: "#e5e7eb", label: "Unavailable / Past" },
              { bg: "#2563EB", border: "#2563EB", label: "Selected" },
            ].map(({ bg, border, label }) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#6b7280" }}>
                <div style={{ width: 13, height: 13, borderRadius: 3, background: bg, border: `1.5px solid ${border}`, flexShrink: 0 }} />
                {label}
              </div>
            ))}
          </div>

          {/* Time selection */}
          {selectedDate && (
            <div style={{ marginTop: 18 }}>
              <label style={fieldLabel}>Start time</label>
              {allRuleHours(selectedDate).length === 0 ? (
                <p style={{ fontSize: 13, color: "#9ca3af" }}>No times available.</p>
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
                  {allRuleHours(selectedDate).map(h => {
                    const avail = startHours.includes(h);
                    const sel   = h === selectedHour;
                    return (
                      <button
                        key={h}
                        disabled={!avail}
                        onClick={() => {
                          setSelectedHour(h);
                          setDuration(Math.max(minBookingHours, Math.min(duration, maxDuration(selectedDate, h))));
                        }}
                        title={avail ? undefined : "Already booked"}
                        style={{
                          padding: "6px 13px",
                          borderRadius: 20,
                          fontSize: 13,
                          fontWeight: sel ? 700 : 400,
                          border: `1.5px solid ${sel ? "#2563eb" : avail ? "#bfdbfe" : "#e5e7eb"}`,
                          background: sel ? "#2563eb" : avail ? "#eff6ff" : "#f9fafb",
                          color: sel ? "#fff" : avail ? "#1d4ed8" : "#9ca3af",
                          cursor: avail ? "pointer" : "not-allowed",
                          textDecoration: avail ? "none" : "line-through",
                          transition: "background 0.1s",
                        }}
                      >
                        {fmt12(h)}
                      </button>
                    );
                  })}
                </div>
              )}

              {selectedHour !== null && (
                <div style={{ marginTop: 14 }}>
                  <label style={fieldLabel}>Duration</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <button
                      onClick={() => setDuration(d => Math.max(minBookingHours, d - 1))}
                      style={counterBtn}
                      disabled={duration <= minBookingHours}
                    >−</button>
                    <span style={{ fontSize: 15, fontWeight: 600, minWidth: 70, textAlign: "center" }}>
                      {duration} {duration === 1 ? "hour" : "hours"}
                    </span>
                    <button
                      onClick={() => setDuration(d => Math.min(maxDuration(selectedDate, selectedHour), d + 1))}
                      style={counterBtn}
                      disabled={duration >= maxDuration(selectedDate, selectedHour)}
                    >+</button>
                  </div>
                  <p style={{ fontSize: 12, color: "#6b7280", margin: "4px 0 0" }}>
                    {fmt12(selectedHour)} – {fmt12(selectedHour + duration)}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Total + book button */}
          {totalCents !== null && selectedDate && selectedHour !== null && (
            <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px solid #e5e7eb" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 14 }}>
                <span>${(pricePerHourCents / 100).toFixed(2)} × {duration} {duration === 1 ? "hr" : "hrs"}</span>
                <span>${(totalCents / 100).toFixed(2)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, fontSize: 13, color: "#6b7280" }}>
                <span>Service fee (6%)</span>
                <span>${(Math.round(totalCents * 0.06) / 100).toFixed(2)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, fontSize: 14, borderTop: "1px solid #e5e7eb", paddingTop: 8 }}>
                <strong>Total</strong>
                <strong>${((totalCents + Math.round(totalCents * 0.06)) / 100).toFixed(2)}</strong>
              </div>
              {bookingError && (
                <p style={{ fontSize: 13, color: "#ef4444", margin: "0 0 10px" }}>{bookingError}</p>
              )}
              {currentUserId && !termsOk ? (
                <p style={{ fontSize: 13, color: "#92400e", background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 6, padding: "10px 14px", margin: 0 }}>
                  You must accept our terms before booking.{" "}
                  <Link to="/dashboard" style={{ color: "#92400e", fontWeight: 600 }}>Go to Dashboard →</Link>
                </p>
              ) : (
                <>
                  <button
                    onClick={handleBook}
                    disabled={booking}
                    style={{ ...primaryBtn, width: "100%" }}
                  >
                    {booking
                      ? "Booking..."
                      : !currentUserId
                      ? "Sign in to book"
                      : requiresApproval
                      ? "Request to book"
                      : "Book now"}
                  </button>
                  {!currentUserId && (
                    <p style={{ fontSize: 12, color: "#6b7280", textAlign: "center", margin: "8px 0 0" }}>
                      You'll be asked to sign in or create a free account.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/* Unauthenticated prompt when no date/time selected yet */}
          {!currentUserId && totalCents === null && (
            <p style={{ fontSize: 13, color: "#6b7280", textAlign: "center", margin: "12px 0 0" }}>
              Select a date and time, then sign in to complete your booking.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function fmt12(hour: number): string {
  const h = hour % 12 || 12;
  const suffix = hour < 12 ? "AM" : "PM";
  return `${h}:00 ${suffix}`;
}

const widgetContainer: React.CSSProperties = {
  border: "1px solid #e5e7eb", borderRadius: 12, padding: 28,
  boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
};
const calGrid: React.CSSProperties = {
  display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 5,
};
const navBtn: React.CSSProperties = {
  background: "none", border: "1px solid #e5e7eb", borderRadius: 6,
  width: 32, height: 32, cursor: "pointer", fontSize: 18, lineHeight: "28px",
};
const dayBtn: React.CSSProperties = {
  padding: 0, border: "none", borderRadius: 6,
  height: 36, fontSize: 13, transition: "background 0.1s, transform 0.1s, box-shadow 0.1s",
};
const fieldLabel: React.CSSProperties = {
  display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4,
};
const counterBtn: React.CSSProperties = {
  width: 34, height: 34, border: "1px solid #d1d5db", borderRadius: 6,
  background: "#fff", cursor: "pointer", fontSize: 18, lineHeight: "30px",
};
const primaryBtn: React.CSSProperties = {
  padding: "12px 24px", background: "#2563EB", color: "#fff",
  border: "none", borderRadius: 8, cursor: "pointer", fontSize: 15, fontWeight: 600,
};
